import { z } from 'zod';
import { AssetTypeSchema, DeviceSlotSchema, TargetSchema } from './domain.js';
export const BULK_RUNS_PATH = '/api/v1/bulk-runs';
export const COSTS_PATH = '/api/v1/costs';
export const BULK_RUN_RESOURCE = 'bulkRun';
export const BULK_RUN_CELL_RESOURCE = 'bulkRunCell';
/**
 * What every cell of one run does.
 *
 * `firstSpread` and `repeat` each create one independent Experiment, and only when the
 * cell starts. `exportPreparation` creates nothing: it packs an Experiment that already
 * exists and whose Play Test Pair was already chosen by hand.
 */
export const BulkOperationKindSchema = z.enum(['firstSpread', 'repeat', 'exportPreparation']);
/**
 * `paused` is a state the run is in, not the absence of work: it survives a restart and
 * ordinary Resume reverses it. `stopped` is terminal for whatever had not started.
 * `ceilingReached` keeps unstarted cells queued and waits for an allowance.
 */
export const BulkRunStateSchema = z.enum(['running', 'paused', 'ceilingReached', 'stopped', 'completed']);
/** Exactly the five states the activity detail names. There is no sixth. */
export const BulkRunCellStateSchema = z.enum(['queued', 'working', 'ready', 'needsAttention', 'stopped']);
/**
 * `No limit` is a choice, not a missing number. A nullable amount would let an omitted
 * field and a deliberate refusal of protection look the same on the wire.
 */
export const BulkCeilingSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('amount'), usd: z.number().nonnegative() }),
    z.object({ kind: z.literal('noLimit') }),
]);
/** One row of the matrix as the operator hands it over. */
export const BulkRowInputSchema = z.object({
    appId: z.string().min(1),
    target: TargetSchema.optional(),
    assetType: AssetTypeSchema,
    deviceSlot: DeviceSlotSchema.optional(),
    /** The one short exception this row is allowed, in the operator's own words. */
    rowException: z.string().optional(),
    /** `Repeat for...`: the Experiment and Candidate this row develops. */
    sourceExperimentId: z.string().min(1).optional(),
    sourceCandidateId: z.string().min(1).optional(),
    /** `exportPreparation`: the exact existing Experiment, pinned before launch. */
    experimentId: z.string().min(1).optional(),
});
export const BulkPreflightRequestSchema = z.object({
    operationKind: BulkOperationKindSchema,
    /** Absent on a repeat means the source Experiment's own Brief, carried in. */
    sharedBrief: z.string().optional(),
    sharedHypothesis: z.string().nullable().optional(),
    /** Explicitly selected shared references. Nothing travels that was not chosen. */
    sharedReferenceLibraryItemIds: z.array(z.string().min(1)).optional(),
    /** `Repeat for...` and `Adapt set`: the one source every destination develops. */
    sourceExperimentId: z.string().min(1).optional(),
    sourceCandidateId: z.string().min(1).optional(),
    rows: z.array(BulkRowInputSchema).min(1),
});
export const LaunchBulkRunRequestSchema = BulkPreflightRequestSchema.extend({
    /** Absent means the estimated upper bound, which is the default ceiling. */
    ceiling: BulkCeilingSchema.optional(),
});
export const BulkPreflightRowSchema = z.object({
    appId: z.string().min(1),
    appName: z.string(),
    target: TargetSchema,
    assetType: AssetTypeSchema,
    deviceSlot: DeviceSlotSchema.nullable(),
    rowException: z.string().nullable(),
    sourceExperimentId: z.string().nullable(),
    sourceCandidateId: z.string().nullable(),
    experimentId: z.string().nullable(),
    /** The idempotent identity of this row inside its run. */
    identityKey: z.string().min(1),
    /** The shared direction with this row's exception applied. What the worker will read. */
    effectiveBrief: z.string(),
    effectiveHypothesis: z.string().nullable(),
    /** Already narrowed to what this row's own App Library holds. */
    referenceLibraryItemIds: z.array(z.string().min(1)),
    estimateLowUsd: z.number().nonnegative(),
    estimateHighUsd: z.number().nonnegative(),
    /** Costs enough more than the rest of the matrix that one range would misread. */
    expensive: z.boolean(),
    /** Why this row cannot run, when it cannot. One plain line, or nothing. */
    refusal: z.string().nullable(),
});
export const BulkPreflightResourceSchema = z.object({
    operationKind: BulkOperationKindSchema,
    sharedBrief: z.string(),
    sharedHypothesis: z.string().nullable(),
    sharedReferenceLibraryItemIds: z.array(z.string().min(1)),
    sourceExperimentId: z.string().nullable(),
    sourceCandidateId: z.string().nullable(),
    rows: z.array(BulkPreflightRowSchema),
    experimentCount: z.number().int().nonnegative(),
    expensiveRowCount: z.number().int().nonnegative(),
    estimateLowUsd: z.number().nonnegative(),
    estimateHighUsd: z.number().nonnegative(),
    /** The upper bound. It becomes the ceiling unless the operator says otherwise. */
    defaultCeilingUsd: z.number().nonnegative(),
});
export const BulkRunResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    operationKind: BulkOperationKindSchema,
    state: BulkRunStateSchema,
    sharedBrief: z.string(),
    sharedHypothesis: z.string().nullable(),
    sharedReferenceLibraryItemIds: z.array(z.string().min(1)),
    sourceExperimentId: z.string().nullable(),
    sourceCandidateId: z.string().nullable(),
    estimateLowUsd: z.number().nonnegative(),
    estimateHighUsd: z.number().nonnegative(),
    /** Null is `No limit`. */
    ceilingUsd: z.number().nonnegative().nullable(),
    /** Committed, the estimate standing in for an actual the provider never reported. */
    spentUsd: z.number().nonnegative(),
    cellCount: z.number().int().nonnegative(),
    readyCount: z.number().int().nonnegative(),
    workingCount: z.number().int().nonnegative(),
    queuedCount: z.number().int().nonnegative(),
    needsAttentionCount: z.number().int().nonnegative(),
    stoppedCount: z.number().int().nonnegative(),
    /**
     * The exact additional sum the remaining cells need. Present only when the ceiling
     * stopped the run, because that is the only moment the number is a decision.
     */
    allowanceNeededUsd: z.number().nonnegative().nullable(),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
    finishedAt: z.string().nullable(),
});
export const BulkRunCellResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    bulkRunId: z.string().min(1),
    position: z.number().int().nonnegative(),
    appId: z.string().min(1),
    appName: z.string(),
    target: TargetSchema,
    assetType: AssetTypeSchema,
    deviceSlot: DeviceSlotSchema.nullable(),
    rowException: z.string().nullable(),
    effectiveBrief: z.string(),
    effectiveHypothesis: z.string().nullable(),
    sourceExperimentId: z.string().nullable(),
    sourceCandidateId: z.string().nullable(),
    /** Null until the cell starts, for every kind that creates its own Experiment. */
    experimentId: z.string().nullable(),
    state: BulkRunCellStateSchema,
    message: z.string().nullable(),
    estimateHighUsd: z.number().nonnegative(),
    expensive: z.boolean(),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
});
/** Rows grouped the way the activity detail reads them: by App. */
export const BulkRunAppGroupSchema = z.object({
    appId: z.string().min(1),
    appName: z.string(),
    cellCount: z.number().int().nonnegative(),
    readyCount: z.number().int().nonnegative(),
});
export const BulkRunDetailSchema = z.object({
    run: BulkRunResourceSchema,
    cells: z.array(BulkRunCellResourceSchema),
    byApp: z.array(BulkRunAppGroupSchema),
});
export const BulkRunListResponseSchema = z.object({ runs: z.array(BulkRunResourceSchema) });
export const ContinueWithAllowanceRequestSchema = z.object({
    /** Absent means exactly what the remaining cells need and not a cent more. */
    additionalUsd: z.number().positive().optional(),
});
/**
 * Compact spend. Two numbers and the window they cover: no tokens, no balance, no
 * invoice and no finance dashboard.
 */
export const CostSummaryResourceSchema = z.object({
    scope: z.enum(['experiment', 'bulkRun', 'workspace']),
    scopeId: z.string().nullable(),
    from: z.string().nullable(),
    to: z.string().nullable(),
    estimatedUsd: z.number().nonnegative(),
    actualUsd: z.number().nonnegative(),
    /** How many Generation Attempts the two numbers are made of. */
    attemptCount: z.number().int().nonnegative(),
    /**
     * Attempts run on a subscription, whose cost is not reported. They are not in the two
     * amounts, which are then the known part of the spend and not all of it.
     */
    subscriptionAttemptCount: z.number().int().nonnegative(),
});
export function bulkRunPath(id) {
    return `${BULK_RUNS_PATH}/${id}`;
}
export function bulkRunPreflightPath() {
    return `${BULK_RUNS_PATH}/preflight`;
}
export function bulkRunRerunPath(id) {
    return `${BULK_RUNS_PATH}/${id}/rerun`;
}
export function bulkRunCommandPath(id, command) {
    return `${BULK_RUNS_PATH}/${id}/${command}`;
}
export const BULK_RUN_COMMANDS = ['pause', 'resume', 'continue-with-allowance', 'stop-remaining', 'retry-failed'];
/**
 * What a Bulk Run is not allowed to do in bulk. Creative judgement stays inside one
 * Experiment, where the Candidate is on the screen beside the decision.
 */
export const NEVER_BULK_ACTIONS = ['keep', 'hide', 'winner', 'playTestPair', 'outcome'];
//# sourceMappingURL=bulk.js.map